<template>
  <div
    v-if="coachingStore.hasAssignedCoach && data.showPage"
    class="i-coaching-home"
  >
    <section
      v-if="
        userStore.isCoachingPaiduser &&
        !userStore.hasCoachingBundle &&
        !(isExecutiveOnce || isExecutiveSubscribedOrCanceled)
      "
      id="promo-bundle"
    >
      <CoachingBundlePromo />
    </section>
    <section
      v-if="data.hasProgress"
      id="progress_tracker"
    >
      <iBaseHeader
        highlight-text="Your Progress"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iProgressTracker />
    </section>
    <section id="upcoming_meetings">
      <iBaseHeader
        highlight-text="Upcoming Meetings"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iUpcomingMeetings />
    </section>
    <section id="coach_profile">
      <iBaseHeader
        highlight-text="Your Coach"
        :version="2"
        font-size-highlighted-text="16px"
        text=""
        align="start"
      />
      <iCoachProfileDetails />
    </section>
    <section
      v-if="!data.hasProgress"
      id="progress_tracker_1"
    >
      <iBaseHeader
        highlight-text="Your Progress"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iProgressTracker @update-progress-status="updateProgressStatus($event)" />
    </section>
    <section
      v-if="data.groupCoachingRegistered"
      id="jca_join"
    >
      <iBaseHeader
        highlight-text="JobTest Career Accelerator"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iJCAJoin :show-header="false" />
    </section>
    <section id="meeting_notes">
      <iBaseHeader
        highlight-text="Meeting Notes"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iMeetingNotes />
    </section>
    <section id="faqs">
      <iBaseHeader
        highlight-text="Frequently Asked Questions"
        :version="2"
        font-size-highlighted-text="16px"
        bottom-margin="30px"
        text=""
        align="start"
      />
      <iFrequentlyAskedQuestions />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive } from "vue"
import { useCoachingStore } from "@/stores/coaching"
import { useUserStore } from "@/stores/user"

import api from "@/services/apiClient"

// COMPONENTS
import iBaseHeader from "@/components/base/BaseHeader.vue"
import iCoachProfileDetails from "@/components/coaching/home/CoachProfileDetails.vue"
import iFrequentlyAskedQuestions from "@/components/coaching/home/FrequentlyAskedQuestions.vue"
import iProgressTracker from "@/components/coaching/home/ProgressTracker.vue"
import iUpcomingMeetings from "@/components/coaching/home/UpcomingMeetings.vue"
import iMeetingNotes from "@/components/coaching/home/MeetingNotes.vue"
import iJCAJoin from "@/components/report/groupCoaching/JCAJoin.vue"
import CoachingBundlePromo from "@/components/coaching/home/CoachingBundlePromo.vue"

// ASSETS
import iconCalendarDate from "@/assets/anchors/icon-calendar-date.svg"
import iconCalendarDateActive from "@/assets/anchors/icon-calendar-date-active.svg"
import iconCalendarRepeat from "@/assets/anchors/icon-calendar-repeat.svg"
import iconCalendarRepeatActive from "@/assets/anchors/icon-calendar-repeat-active.svg"
import iconBioActive from "@/assets/anchors/icon-user-square-active.svg"
import iconBio from "@/assets/anchors/icon-user-square.svg"
import iconProgressTracker from "@/assets/anchors/icon-route.svg"
import iconProgressTrackerActive from "@/assets/anchors/icon-route-blue.svg"
import iconMeetingNote from "@/assets/anchors/icon-profile-study-black.svg"
import iconMeetingNoteActive from "@/assets/anchors/icon-profile-study-blue.svg"
import iconFAQActive from "@/assets/anchors/icon-message-question-square-active.svg"
import iconFAQ from "@/assets/anchors/icon-message-question-square.svg"

// TYPES
import type { PageAnchors } from "@/types/pageAnchors"

// EMITS
const emit = defineEmits(["load-anchors"])

// STORES
const userStore = useUserStore()
const coachingStore = useCoachingStore()

// CONSTANTS
const urlWithTag = window.location.hash !== ""

// COMPUTED
const isExecutiveOnce = computed(() => userStore.isCoachingOnce && userStore.hasPurchasedExecutiveDiscovery)
const isExecutiveSubscribedOrCanceled = computed(
  () => userStore.isCoachingExecutiveOrProfessionalSubscriber || userStore.hasPurchasedExecutiveSubscription
)
const constantPageAnchors = computed(() => {
  return {
    progress_tracker: {
      text: "Your Progress",
      icon: iconProgressTracker,
      iconActive: iconProgressTrackerActive,
      isHidden: !data.hasProgress
    },
    upcoming_meetings: {
      text: "Upcoming Meetings",
      icon: iconCalendarDate,
      iconActive: iconCalendarDateActive
    },
    coach_profile: {
      text: "Your Coach",
      icon: iconBio,
      iconActive: iconBioActive
    },
    progress_tracker_1: {
      text: "Your Progress",
      icon: iconProgressTracker,
      iconActive: iconProgressTrackerActive,
      isHidden: data.hasProgress
    },
    jca_join: {
      text: "Career Accelerator",
      icon: iconCalendarRepeat,
      iconActive: iconCalendarRepeatActive,
      isHidden: !data.groupCoachingRegistered
    },
    meeting_notes: {
      text: "Meeting Notes",
      icon: iconMeetingNote,
      iconActive: iconMeetingNoteActive
    },
    faqs: {
      text: "FAQs",
      icon: iconFAQ,
      iconActive: iconFAQActive
    }
  } as PageAnchors
})

// DATA
const data = reactive({
  showPage: false,
  groupCoachingRegistered: false,
  lsChecked: {
    reminder: localStorage.getItem("checklist-reminder-checked"),
    resources: localStorage.getItem("checklist-resources-checked"),
    video: localStorage.getItem("checklist-video-checked")
  },
  hasProgress: false
})

// FUNCTIONS
const fetchGroupCoachingStatus = async () => {
  if (userStore.getUserId) {
    try {
      const groupCoachingData = await api.client.checkGroupCoaching(userStore.getUserId)
      data.groupCoachingRegistered = groupCoachingData.registered
    } catch (error) {
      console.error("Error fetching user's group coaching status:", error)
    }
  }
}
const handleChecklistItemChanged = (event: Event) => {
  function isCustomEvent(event: Event): event is CustomEvent {
    return "detail" in event
  }
  if (!isCustomEvent(event)) return

  const key = event.detail.key as "reminder" | "resources" | "video"
  data.lsChecked[key] = localStorage.getItem(`checklist-${key}-checked`)
  return
}
const updateProgressStatus = (status: boolean) => {
  data.hasProgress = status
  emit("load-anchors", { anchors: constantPageAnchors.value, autoScroll: urlWithTag })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchGroupCoachingStatus()
  if (!coachingStore.hasAssignedCoach) await coachingStore.loadAssignedCoach()
  emit("load-anchors", { anchors: constantPageAnchors.value, autoScroll: urlWithTag })
  data.showPage = true

  window.addEventListener("localstorage-checklist-item-changed", handleChecklistItemChanged)
})

onBeforeUnmount(() => {
  window.removeEventListener("localstorage-checklist-item-changed", handleChecklistItemChanged)
})
</script>

<style scoped lang="scss">
.i-coaching-home {
  @include flex($direction: column, $justify: start, $align: stretch, $gap: 30px);

  @media screen and (min-width: $breakpoint-xl) {
    margin-left: 24px;
  }

  section {
    scroll-margin-top: 90px;
  }
}
</style>
